import { useState, type FormEvent } from 'react'
import { describeError } from '@/lib/auth'
import { useGuildStore } from '@/store/guilds'
import { THREAD_ARCHIVE_OPTIONS, defaultThreadName } from '@/lib/guildUi'
import type { Channel, Message } from '@/types/api'
import { renderMarkdown } from '@/lib/markdown'
import { mentionNames } from '@/lib/mentions'
import { Modal } from '../settings/Modal'
import styles from './ThreadModal.module.css'
import { Select } from '../Select'

interface Props {
  channelId: string
  channelName: string
  /** Сообщение-начало ветки; null — ветка без сообщения. */
  message: Message | null
  onClose: () => void
  onCreated: (thread: Channel) => void
}

/** Создание ветки: название и срок автоархива. */
export function ThreadModal({ channelId, channelName, message, onClose, onCreated }: Props) {
  const createThread = useGuildStore((s) => s.createThread)
  const [name, setName] = useState(message ? defaultThreadName(message.content) : '')
  const [duration, setDuration] = useState(1440)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setBusy(true)
    setError(null)
    try {
      onCreated(await createThread(channelId, message?.id ?? null, { name: name.trim(), auto_archive_duration: duration }))
    } catch (err) {
      setError(describeError(err).message)
      setBusy(false)
    }
  }

  return (
    <Modal title="Новая ветка" onClose={onClose}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <p className={styles.hint}>
          {message ? 'Ветка от сообщения в' : 'Ветка в'} <strong>#{channelName}</strong>. Её увидят все, кто видит канал; писать в ней смогут те, кому разрешены сообщения в ветках.
        </p>
        {message?.content ? <blockquote className={styles.quote}>{renderMarkdown(message.content, { userName: mentionNames(message) })}</blockquote> : null}
        <label className={styles.field}>
          <span className={styles.label}>Название</span>
          <input className={styles.input} value={name} maxLength={100} autoFocus placeholder="О чём ветка" onChange={(e) => setName(e.target.value)} />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>В архив без активности через</span>
          <Select value={duration} options={THREAD_ARCHIVE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} onChange={setDuration} />
        </label>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <div className={styles.buttons}>
          <button type="button" className={styles.cancel} onClick={onClose}>Отмена</button>
          <button type="submit" className={styles.primary} disabled={busy || !name.trim()}>{busy ? 'Создаём…' : 'Создать ветку'}</button>
        </div>
      </form>
    </Modal>
  )
}
